import gql from "graphql-tag";
import GraphQLClient, { RequestParam } from "../api";
import {
  Assist,
  CreateFeedbackManuscriptInput,
  CreateMatchingCompanyInput,
  CreateGenerateCatchcopyInput,
  CreateGenerateSummaryInput,
  CreateFeedbackThemeInput,
  GetAssistLogInput,
  ListAssistLogInput,
} from "../models/assist";
import { logError, createApiLogContext } from "../utils/logger";

// ////////////////////
// Type Definitions
// ////////////////////

interface GraphQLError {
  message: string;
  response?: {
    status: number;
  };
}

// ////////////////////
// Interface Definition
// ////////////////////

interface IAssistClient {
  createFeedbackManuscript: (input: CreateFeedbackManuscriptInput) => Promise<Assist>;
  createMatchingCompany: (input: CreateMatchingCompanyInput) => Promise<Assist>;
  createGenerateCatchcopy: (input: CreateGenerateCatchcopyInput) => Promise<Assist>;
  createGenerateSummary: (input: CreateGenerateSummaryInput) => Promise<Assist>;
  createFeedbackTheme: (input: CreateFeedbackThemeInput) => Promise<Assist>;
  getAssistLog: (input: GetAssistLogInput) => Promise<Assist>;
  listAssistLog: (input: ListAssistLogInput) => Promise<Assist[]>;
}

// ////////////////////
// AssistClient Implementation
// ////////////////////

export class AssistClient implements IAssistClient {
  /**
   * 原稿感想生成を依頼
   * @param input 原稿感想生成リクエスト
   * @returns Assist処理ログ
   */
  async createFeedbackManuscript(input: CreateFeedbackManuscriptInput): Promise<Assist> {
    const params: RequestParam = {
      query: createFeedbackManuscriptMutation,
      variables: {
        input,
      },
    };

    try {
      const { createFeedbackManuscript } = await GraphQLClient.request(params);
      return createFeedbackManuscript;
    } catch (error: unknown) {
      // API仕様に基づくエラーハンドリング
      this.handleAssistError(error as GraphQLError | Error);
      throw error;
    }
  }

  /**
   * 応募先マッチングを依頼
   * @param input 応募先マッチングリクエスト
   * @returns Assist処理ログ
   */
  async createMatchingCompany(input: CreateMatchingCompanyInput): Promise<Assist> {
    const params: RequestParam = {
      query: createMatchingCompanyMutation,
      variables: {
        input,
      },
    };

    try {
      const { createMatchingCompany } = await GraphQLClient.request(params);
      return createMatchingCompany;
    } catch (error: unknown) {
      // API仕様に基づくエラーハンドリング
      this.handleAssistError(error as GraphQLError | Error);
      throw error;
    }
  }

  /**
   * キャッチコピー生成を依頼
   * @param input キャッチコピー生成リクエスト
   * @returns Assist処理ログ
   */
  async createGenerateCatchcopy(input: CreateGenerateCatchcopyInput): Promise<Assist> {
    const params: RequestParam = {
      query: createGenerateCatchcopyMutation,
      variables: {
        input,
      },
    };

    try {
      const { createGenerateCatchcopy } = await GraphQLClient.request(params);
      return createGenerateCatchcopy;
    } catch (error: unknown) {
      // API仕様に基づくエラーハンドリング
      this.handleAssistError(error as GraphQLError | Error);
      throw error;
    }
  }

  /**
   * あらすじ生成を依頼
   * @param input あらすじ生成リクエスト
   * @returns Assist処理ログ
   */
  async createGenerateSummary(input: CreateGenerateSummaryInput): Promise<Assist> {
    const params: RequestParam = {
      query: createGenerateSummaryMutation,
      variables: {
        input,
      },
    };

    try {
      const { createGenerateSummary } = await GraphQLClient.request(params);
      return createGenerateSummary;
    } catch (error: unknown) {
      // API仕様に基づくエラーハンドリング
      this.handleAssistError(error as GraphQLError | Error);
      throw error;
    }
  }

  /**
   * 企画（theme）レビューを依頼
   * @param input 企画レビューリクエスト
   * @returns Assist処理ログ
   */
  async createFeedbackTheme(input: CreateFeedbackThemeInput): Promise<Assist> {
    const params: RequestParam = {
      query: createFeedbackThemeMutation,
      variables: {
        input,
      },
    };

    try {
      const { createFeedbackTheme } = await GraphQLClient.request(params);
      return createFeedbackTheme;
    } catch (error: unknown) {
      // API仕様に基づくエラーハンドリング
      this.handleAssistError(error as GraphQLError | Error);
      throw error;
    }
  }

  /**
   * 特定のアシストログを取得
   * @param input アシストログ取得リクエスト
   * @returns アシスト処理ログ（結果含む）
   */
  async getAssistLog(input: GetAssistLogInput): Promise<Assist> {
    const params: RequestParam = {
      query: getAssistLogQuery,
      variables: {
        input,
      },
    };

    try {
      const { getAssistLog } = await GraphQLClient.request(params);
      return getAssistLog;
    } catch (error: any) {
      this.handleAssistError(error);
      throw error;
    }
  }

  /**
   * アシストログ一覧を取得
   * @param input アシストログ一覧取得リクエスト
   * @returns アシスト処理ログの配列
   */
  async listAssistLog(input: ListAssistLogInput): Promise<Assist[]> {
    const params: RequestParam = {
      query: listAssistLogQuery,
      variables: {
        input,
      },
    };

    try {
      const { listAssistLog } = await GraphQLClient.request(params);
      return listAssistLog;
    } catch (error: any) {
      this.handleAssistError(error);
      throw error;
    }
  }

  /**
   * Assist API固有のエラーハンドリング
   * @param error GraphQLエラー
   */
  private handleAssistError(error: GraphQLError | Error): void {
    // ASSIST_API_SPECIFICATION.mdで定義されたエラーメッセージをチェック
    if ("message" in error && error.message) {
      if (error.message.includes("Fishの残高が不足")) {
        logError(createApiLogContext("Assist", "Fish残高不足"), error.message, error);
      } else if (error.message.includes("GPT Handler")) {
        logError(createApiLogContext("Assist", "AI処理エラー"), error.message, error);
      } else if ("response" in error && error.response?.status === 401) {
        logError(createApiLogContext("Assist", "認証エラー"), error.message, error);
      } else {
        logError(createApiLogContext("Assist", "予期しないエラー"), error.message, error);
      }
    }
  }
}

// ////////////////////
// GraphQL Queries and Mutations
// ////////////////////

/** 原稿感想生成ミューテーション */
const createFeedbackManuscriptMutation = gql`
  mutation CreateFeedbackManuscript($input: CreateFeedbackManuscriptInput!) {
    createFeedbackManuscript(input: $input) {
      userId
      novelId
      functionType
      assistId
      writerType
      feedbackManuscriptMode
      requestTitle
      completed
      isError
      createdAt
      updatedAt
    }
  }
`;

/** 応募先マッチングミューテーション */
const createMatchingCompanyMutation = gql`
  mutation CreateMatchingCompany($input: CreateMatchingCompanyInput!) {
    createMatchingCompany(input: $input) {
      userId
      novelId
      functionType
      assistId
      writerType
      requestTitle
      completed
      isError
      createdAt
      updatedAt
    }
  }
`;

/** キャッチコピー生成ミューテーション */
const createGenerateCatchcopyMutation = gql`
  mutation CreateGenerateCatchcopy($input: CreateGenerateCatchcopyInput!) {
    createGenerateCatchcopy(input: $input) {
      userId
      novelId
      functionType
      assistId
      writerType
      requestTitle
      completed
      isError
      createdAt
      updatedAt
    }
  }
`;

/** あらすじ生成ミューテーション */
const createGenerateSummaryMutation = gql`
  mutation CreateGenerateSummary($input: CreateGenerateSummaryInput!) {
    createGenerateSummary(input: $input) {
      userId
      novelId
      functionType
      assistId
      writerType
      generateSummaryMode
      requestTitle
      completed
      isError
      createdAt
      updatedAt
    }
  }
`;

/** 企画（theme）レビューミューテーション */
const createFeedbackThemeMutation = gql`
  mutation CreateFeedbackTheme($input: CreateFeedbackThemeInput!) {
    createFeedbackTheme(input: $input) {
      userId
      novelId
      functionType
      assistId
      writerType
      requestTitle
      completed
      isError
      createdAt
      updatedAt
    }
  }
`;

/** 特定のアシストログ取得クエリ */
const getAssistLogQuery = gql`
  query GetAssistLog($input: GetAssistLogInput!) {
    getAssistLog(input: $input) {
      userId
      novelId
      functionType
      assistId
      writerType
      feedbackManuscriptMode
      generateSummaryMode
      requestTitle
      result {
        message
        html
      }
      completed
      isError
      createdAt
      updatedAt
    }
  }
`;

/** アシストログ一覧取得クエリ */
const listAssistLogQuery = gql`
  query ListAssistLog($input: ListAssistLogInput!) {
    listAssistLog(input: $input) {
      userId
      novelId
      functionType
      assistId
      writerType
      feedbackManuscriptMode
      generateSummaryMode
      requestTitle
      result {
        message
        html
      }
      completed
      isError
      createdAt
      updatedAt
    }
  }
`;
